import { makeStyles } from "@material-ui/core";
import {
  auditLogsLocationRate,
  auditLogsPriceRate
} from "@saleor/auditLogs/urls";
import { isSuperUser } from "@saleor/auth/misc";
import CardSpacer from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import Grid from "@saleor/components/Grid";
import MoreActions from "@saleor/components/MoreActions";
import { MultiAutocompleteChoiceType } from "@saleor/components/MultiAutocompleteSelectField";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { SingleAutocompleteChoiceType } from "@saleor/components/SingleAutocompleteSelectField";
import { configurationOpsMenuUrl } from "@saleor/configuration_operations";
import { ShippingErrorFragment } from "@saleor/fragments/types/ShippingErrorFragment";
import {
  ShippingZoneDetailsFragment,
  ShippingZoneDetailsFragment_warehouses
} from "@saleor/fragments/types/ShippingZoneDetailsFragment";
import { SubmitPromise } from "@saleor/hooks/useForm";
import useNavigator from "@saleor/hooks/useNavigator";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import useUser from "@saleor/hooks/useUser";
import createMultiAutocompleteSelectHandler from "@saleor/utils/handlers/multiAutocompleteSelectChangeHandler";
import React from "react";

import { FetchMoreProps, SearchProps } from "../../../types";
import { ShippingMethodTypeEnum } from "../../../types/globalTypes";
import ShippingZoneInfo from "../ShippingZoneInfo";
import ShippingZoneRates from "../ShippingZoneRates";
import ShippingZoneWarehouses from "../ShippingZoneWarehouses";

const useStyles = makeStyles(
  _ => ({
    pageHeader: {
      display: "flex",
      flexDirection: "row",
      marginTop: "10px"
    },
    pageHeaderRight: {
      "& button": {
        background: "white !important"
      },
      marginLeft: "auto"
    }
  }),
  { name: "ShippingZoneDetailsPage" }
);

export interface FormData {
  name: string;
  warehouses: string[];
}

export interface ShippingZoneDetailsPageProps
  extends FetchMoreProps,
    SearchProps {
  disabled: boolean;
  errors: ShippingErrorFragment[];
  saveButtonBarState: ConfirmButtonTransitionState;
  shippingZone: ShippingZoneDetailsFragment;
  warehouses: ShippingZoneDetailsFragment_warehouses[];
  onBack: () => void;
  onCountryAdd: () => void;
  onCountryRemove: (code: string) => void;
  onDelete: () => void;
  onSubmit: (data: FormData) => SubmitPromise;
  onRateRemove: (rateId: string) => void;
  onPriceRateAdd: () => void;
  onPriceRateEdit: (id: string) => void;
  onLocationRateAdd: () => void;
  onLocationRateEdit: (id: string) => void;
  onWarehouseAdd: () => void;
  onWeightRateAdd: () => void;
  onWeightRateEdit: (id: string) => void;
  onSetAsActive: (data: any) => void;
}

function warehouseToChoice(
  warehouse: Record<"id" | "name", string>
): SingleAutocompleteChoiceType {
  return {
    label: warehouse.name,
    value: warehouse.id
  };
}

const ShippingZoneDetailsPage: React.FC<ShippingZoneDetailsPageProps> = ({
  disabled,
  errors,
  hasMore,
  loading,
  onFetchMore,
  onSearchChange,
  onBack,
  onDelete,
  onSubmit,
  onRateRemove,
  onPriceRateAdd,
  onPriceRateEdit,
  onLocationRateAdd,
  onLocationRateEdit,
  onWarehouseAdd,
  onWeightRateAdd,
  onWeightRateEdit,
  saveButtonBarState,
  shippingZone,
  warehouses,
  onSetAsActive
}) => {
  const user = useUser();
  const classes = useStyles({});
  const navigate = useNavigator();

  const initialForm: FormData = {
    name: shippingZone?.name || "",
    warehouses: shippingZone?.warehouses?.map(warehouse => warehouse.id) || []
  };
  const [warehouseDisplayValues, setWarehouseDisplayValues] = useStateFromProps<
    MultiAutocompleteChoiceType[]
  >(
    shippingZone?.warehouses?.map(warehouse => ({
      label: warehouse.name,
      value: warehouse.id
    })) || []
  );

  const warehouseChoices = warehouses.map(warehouseToChoice);
  const breadcrumbDirectory = [
    {
      name: "Configuration",
      url: configurationOpsMenuUrl
    },
    {
      name: "Shipping Methods"
    }
  ];

  return (
    <Form initial={initialForm} onSubmit={onSubmit}>
      {({ change, data, hasChanged, submit, toggleValue }) => {
        const handleWarehouseChange = createMultiAutocompleteSelectHandler(
          toggleValue,
          setWarehouseDisplayValues,
          warehouseDisplayValues,
          warehouseChoices
        );

        return (
          <Container>
            <PageBreadcrumbNavigation items={breadcrumbDirectory} />
            <div className={classes.pageHeader}>
              <PageBackNavigation onBack={() => onBack()} />
              <PageHeader title="Shipping Methods"></PageHeader>
              <MoreActions
                id={"shippingZoneDetailsPageMoreActions"}
                className={classes.pageHeaderRight}
                items={[
                  {
                    action: () =>
                      navigate(auditLogsLocationRate + shippingZone?.id),
                    header: "Activity Logs",
                    id: "shippingZoneDetailsPageLocationRate",
                    label: "Location Range Rates"
                  },
                  {
                    action: () =>
                      navigate(auditLogsPriceRate + shippingZone?.id),
                    header: "Activity Logs",
                    id: "shippingZoneDetailsPagePriceRate",
                    label: "Price Based Rates"
                  }
                ]}
              />
            </div>
            <Grid>
              <div>
                <ShippingZoneInfo
                  data={data}
                  disabled={disabled}
                  errors={errors}
                  onChange={change}
                />
                <CardSpacer />
                <ShippingZoneRates
                  disabled={disabled}
                  onRateAdd={onPriceRateAdd}
                  onRateEdit={onPriceRateEdit}
                  onRateRemove={onRateRemove}
                  rates={shippingZone?.shippingMethods?.filter(
                    method => method.type === ShippingMethodTypeEnum.PRICE
                  )}
                  variant="price"
                  shippingZoneId={shippingZone?.id}
                  onSetAsActive={onSetAsActive}
                />
                <CardSpacer />
                <ShippingZoneRates
                  disabled={disabled}
                  onRateAdd={onLocationRateAdd}
                  onRateEdit={onLocationRateEdit}
                  onRateRemove={onRateRemove}
                  rates={shippingZone?.shippingMethods?.filter(
                    method => method.type === ShippingMethodTypeEnum.LOCATION
                  )}
                  variant="location"
                  shippingZoneId={shippingZone?.id}
                  onSetAsActive={onSetAsActive}
                />
                <CardSpacer />
                {isSuperUser(user?.user) && (
                  <ShippingZoneRates
                    disabled={disabled}
                    onRateAdd={onWeightRateAdd}
                    onRateEdit={onWeightRateEdit}
                    onRateRemove={onRateRemove}
                    rates={shippingZone?.shippingMethods?.filter(
                      method => method.type === ShippingMethodTypeEnum.WEIGHT
                    )}
                    variant="weight"
                    shippingZoneId={shippingZone?.id}
                    onSetAsActive={onSetAsActive}
                  />
                )}
              </div>
              <div>
                {isSuperUser(user?.user) && (
                  <ShippingZoneWarehouses
                    data={data}
                    displayValues={warehouseDisplayValues}
                    hasMore={hasMore}
                    loading={loading}
                    onChange={handleWarehouseChange}
                    onFetchMore={onFetchMore}
                    onSearchChange={onSearchChange}
                    onWarehouseAdd={onWarehouseAdd}
                    warehouses={warehouseChoices}
                  />
                )}
              </div>
            </Grid>
            <SaveButtonBar
              disabled={disabled || !hasChanged}
              onCancel={onBack}
              onDelete={onDelete}
              onSave={submit}
              state={saveButtonBarState}
            />
          </Container>
        );
      }}
    </Form>
  );
};
ShippingZoneDetailsPage.displayName = "ShippingZoneDetailsPage";
export default ShippingZoneDetailsPage;
